// Connecting a mailbox (R-M6): the address fills the servers of the known providers, the app password goes to the server once and
// is kept there encrypted; the login is checked before saving. For a connected mailbox — its state, «Обновить», «Проверить снова».

import { useEffect, useState } from 'react';
import { uuidv7 } from '@confdeck/core-shared';
import { useSync } from '../../sync/react.tsx';
import { Button, Dialog, Field, Input, Switch, useToast } from '../../ui/primitives.tsx';
import { fullTime } from './model.ts';
import type { Mailbox } from './use-letters.ts';
import rs from '../../record/record.module.css';
import s from './mail.module.css';

/** Servers of the providers people use most; anything else is typed by hand. */
const PROVIDERS: Record<string, { imap: string; smtp: string }> = {
  'yandex.ru': { imap: 'imap.yandex.ru', smtp: 'smtp.yandex.ru' }, 'ya.ru': { imap: 'imap.yandex.ru', smtp: 'smtp.yandex.ru' },
  'mail.ru': { imap: 'imap.mail.ru', smtp: 'smtp.mail.ru' }, 'bk.ru': { imap: 'imap.mail.ru', smtp: 'smtp.mail.ru' }, 'inbox.ru': { imap: 'imap.mail.ru', smtp: 'smtp.mail.ru' },
  'gmail.com': { imap: 'imap.gmail.com', smtp: 'smtp.gmail.com' },
};

export function ConnectDialog({ mailbox, onClose }: { mailbox: Mailbox | null; onClose(): void }) {
  const client = useSync();
  const toast = useToast();
  const d = mailbox?.rec.data ?? {};
  const [v, setV] = useState({
    name: String(d.name ?? ''), address: String(d.address ?? ''), imapHost: String(d.imap_host ?? ''), imapPort: String(d.imap_port ?? 993),
    smtpHost: String(d.smtp_host ?? ''), smtpPort: String(d.smtp_port ?? 465), login: String(d.login ?? ''), password: '', tls: d.tls !== false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secrets, setSecrets] = useState(true);
  useEffect(() => { fetch('/api/mail/status').then((r) => r.json()).then((j: { secrets: boolean }) => setSecrets(j.secrets)).catch(() => {}); }, []);
  const set = (patch: Partial<typeof v>) => { setV((p) => ({ ...p, ...patch })); setError(null); };
  const guess = () => {
    const p = PROVIDERS[v.address.split('@')[1]?.toLowerCase() ?? ''];
    if (p) set({ imapHost: v.imapHost || p.imap, smtpHost: v.smtpHost || p.smtp });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch('/api/mail/connect', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({
        id: mailbox?.id ?? uuidv7(), name: v.name, address: v.address, imapHost: v.imapHost, imapPort: Number(v.imapPort), smtpHost: v.smtpHost,
        smtpPort: Number(v.smtpPort), login: v.login || undefined, password: v.password || undefined, tls: v.tls,
      }) });
      const j = await r.json() as { error?: { message: string } };
      if (!r.ok) { setError(j.error?.message ?? 'Ящик не подключился'); return; }
      toast({ kind: 'info', text: mailbox ? 'Ящик обновлён' : 'Ящик подключён — письма появятся через минуту' });
      onClose();
    } catch { setError('Нет связи с сервером'); }
    finally { setBusy(false); }
  };
  const syncNow = async () => {
    if (!mailbox) return;
    setBusy(true);
    if (mailbox.syncState === 'error') await client.run('mail.resumeSync', { id: mailbox.id });
    const r = await fetch(`/api/mail/mailboxes/${mailbox.id}/sync`, { method: 'POST' });
    const j = await r.json() as { received?: number; sent?: number; error?: { message: string } };
    setBusy(false);
    if (!r.ok) { setError(j.error?.message ?? 'Синхронизация не прошла'); return; }
    toast({ kind: 'info', text: `Готово: новых писем ${j.received ?? 0}, отправлено ${j.sent ?? 0}` });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} label={mailbox ? `Ящик ${mailbox.name}` : 'Подключить ящик'}>
      <form className={rs.dialog} onSubmit={(e) => void submit(e)}>
        <h2 className={rs.dialogTitle}>{mailbox ? mailbox.name : 'Подключить почтовый ящик'}</h2>
        {mailbox ? (
          <p className={rs.dialogText}>
            {mailbox.syncState === 'error' ? `Синхронизация остановлена после ошибок: ${mailbox.syncError}` : mailbox.lastSync ? `Синхронизирован ${fullTime(mailbox.lastSync)}` : 'Ещё не синхронизировался'}
            {mailbox.syncState !== 'error' && mailbox.syncError ? ` · последняя ошибка: ${mailbox.syncError}` : ''}
          </p>
        ) : (
          <p className={rs.dialogText}>Для Яндекса, Mail.ru и Gmail нужен пароль приложения: его создают в настройках безопасности почты. Пароль хранится на сервере в зашифрованном виде.</p>
        )}
        {!secrets && <div className={rs.dialogError} role="alert">На сервере не задан ключ шифрования — пароль сохранить нельзя. Обратитесь к администратору.</div>}
        <Field label="Адрес"><Input type="email" required value={v.address} disabled={!!mailbox} onChange={(e) => set({ address: e.target.value })} onBlur={guess} placeholder="team@company.ru" /></Field>
        <Field label="Название" hint="Как ящик называется в списке"><Input value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="Отдел партнёров" /></Field>
        <div className={s.formGrid}>
          <Field label="Сервер IMAP"><Input required value={v.imapHost} onChange={(e) => set({ imapHost: e.target.value })} placeholder="imap.yandex.ru" /></Field>
          <Field label="Порт"><Input inputMode="numeric" value={v.imapPort} onChange={(e) => set({ imapPort: e.target.value })} /></Field>
          <Field label="Сервер SMTP"><Input required value={v.smtpHost} onChange={(e) => set({ smtpHost: e.target.value })} placeholder="smtp.yandex.ru" /></Field>
          <Field label="Порт"><Input inputMode="numeric" value={v.smtpPort} onChange={(e) => set({ smtpPort: e.target.value })} /></Field>
        </div>
        <Field label="Логин" hint="Обычно совпадает с адресом"><Input value={v.login} onChange={(e) => set({ login: e.target.value })} placeholder={v.address} /></Field>
        <Field label="Пароль приложения" hint={mailbox ? 'Оставьте пустым, чтобы не менять' : undefined}>
          <Input type="password" autoComplete="new-password" required={!mailbox} value={v.password} onChange={(e) => set({ password: e.target.value })} />
        </Field>
        <Switch checked={v.tls} onChange={(tls) => set({ tls })} label="Защищённое соединение">Защищённое соединение (SSL/TLS)</Switch>
        {error && <div className={rs.dialogError} role="alert">{error}</div>}
        <div className={rs.dialogFoot}>
          {mailbox && <Button type="button" icon="refresh" disabled={busy} onClick={() => void syncNow()}>{mailbox.syncState === 'error' ? 'Проверить снова' : 'Обновить сейчас'}</Button>}
          <span className={s.spacer} />
          <Button type="button" onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="primary" disabled={busy || !secrets}>{busy ? 'Проверяю…' : mailbox ? 'Сохранить' : 'Подключить'}</Button>
        </div>
      </form>
    </Dialog>
  );
}
